import { useState } from "react";
import { formatDistanceToNow } from "date-fns";
import { Check, Undo2, Trash2, Reply } from "lucide-react";
import { Button, buttonVariants } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/hooks/useAuth";
import {
  useResolveComment,
  useUnresolveComment,
  useDeleteComment,
  type Comment,
} from "@/hooks/useComments";
import { CommentInput } from "@/components/comments/CommentInput";
import { cn } from "@/lib/utils";

function getInitials(firstName: string, lastName: string) {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
}

function highlightMentions(content: string): React.ReactNode {
  const parts = content.split(/(@\w+(?:\s\w+)?)/g);
  return parts.map((part, i) => {
    if (part.startsWith("@")) {
      return (
        <span key={i} className="text-primary font-medium">
          {part}
        </span>
      );
    }
    return part;
  });
}

interface CommentItemProps {
  comment: Comment;
  isReply?: boolean;
}

export function CommentItem({ comment, isReply }: CommentItemProps) {
  const { user } = useAuth();
  const resolveComment = useResolveComment();
  const unresolveComment = useUnresolveComment();
  const deleteComment = useDeleteComment();
  const [showReplyInput, setShowReplyInput] = useState(false);

  const isOwn = user?.id === comment.author_id;
  const authorName = comment.author
    ? `${comment.author.first_name} ${comment.author.last_name}`
    : "Unknown";

  return (
    <div data-testid={`comment-item-${comment.id}`}>
      <div
        className={cn(
          "group flex gap-2.5 py-2.5 px-1",
          comment.is_resolved && "opacity-50",
        )}
      >
        <Avatar
          className={cn("shrink-0 mt-0.5", isReply ? "h-6 w-6" : "h-7 w-7")}
        >
          <AvatarFallback
            className={cn(isReply ? "text-[9px]" : "text-[10px]")}
          >
            {comment.author
              ? getInitials(comment.author.first_name, comment.author.last_name)
              : "?"}
          </AvatarFallback>
        </Avatar>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span
              className={cn(
                "font-medium truncate",
                isReply ? "text-xs" : "text-sm",
              )}
            >
              {authorName}
            </span>
            <span className="text-xs text-muted-foreground shrink-0">
              {formatDistanceToNow(new Date(comment.created_at), {
                addSuffix: true,
              })}
            </span>
          </div>

          <p
            data-testid={`comment-content-${comment.id}`}
            className={cn(
              "mt-0.5 whitespace-pre-wrap break-words",
              isReply ? "text-xs" : "text-sm",
              comment.is_resolved && "line-through",
            )}
          >
            {highlightMentions(comment.content)}
          </p>

          {comment.is_resolved && comment.resolver && (
            <p className="text-xs text-muted-foreground mt-1">
              Resolved by {comment.resolver.first_name}{" "}
              {comment.resolver.last_name}
            </p>
          )}

          <div className="flex items-center gap-1 mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
            {!comment.is_resolved && (
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={() => setShowReplyInput(true)}
                title="Reply"
                aria-label="Reply"
                data-testid={`comment-reply-${comment.id}`}
              >
                <Reply className="h-3.5 w-3.5" />
              </Button>
            )}

            {comment.is_resolved ? (
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={() => unresolveComment.mutate(comment.id)}
                title="Unresolve"
                aria-label="Unresolve"
                data-testid={`comment-unresolve-${comment.id}`}
              >
                <Undo2 className="h-3.5 w-3.5" />
              </Button>
            ) : (
              !isReply && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={() => resolveComment.mutate(comment.id)}
                  title="Resolve"
                  aria-label="Resolve"
                  data-testid={`comment-resolve-${comment.id}`}
                >
                  <Check className="h-3.5 w-3.5" />
                </Button>
              )
            )}

            {isOwn && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 text-destructive hover:text-destructive"
                    title="Delete"
                    aria-label="Delete"
                    data-testid={`comment-delete-${comment.id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete comment?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will permanently delete this comment
                      {comment.replies.length > 0 ? " and all its replies" : ""}
                      . This action cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      className={buttonVariants({ variant: "destructive" })}
                      onClick={() => deleteComment.mutate(comment.id)}
                      data-testid={`comment-confirm-delete-${comment.id}`}
                    >
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        </div>
      </div>

      {/* Replies */}
      {comment.replies.length > 0 && (
        <div className="ml-6 border-l pl-2">
          {comment.replies.map((reply) => (
            <CommentItem key={reply.id} comment={reply} isReply />
          ))}
        </div>
      )}

      {/* Reply input */}
      {showReplyInput && (
        <div className="ml-6 pl-2">
          <CommentInput
            entityType={comment.entity_type}
            entityId={comment.entity_id}
            parentId={comment.id}
            compact
            autoFocus
            onCancel={() => setShowReplyInput(false)}
          />
        </div>
      )}
    </div>
  );
}
